<script setup lang="ts">
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { initializeOptionIfNeeded } from '@/happyTheme/utils/product/button/initializeOptionIfNeeded'
import { decrementOptionValueHappy } from '@/happyTheme/utils/product/optionValue/decrementOptionValueHappy'
import { incrementOptionValueHappy } from '@/happyTheme/utils/product/optionValue/incrementOptionValueHappy'
import type { Option } from '@/storage/products'
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps<{
    maxQuantity?: number
    currentQuantity: number
    option: Option
    currentOptionValueId: number
}>()

const counterOptionValue = ref(props.currentQuantity)

watch(
    () =>
        [
            selectOptionsListHappy.value[props.option.option_id]!,
            showSelectedModalProductHappy.value
        ] as const,
    ([newValue, newShow]) => {
        if (!newValue) return

        if (!selectOptionsListHappy.value[props.option.option_id]) return

        const optionValueFindReset = [
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_free,
            ...selectOptionsListHappy.value[props.option.option_id].selected_values
                .checkbox_option.option_value_paid
        ].filter((o) => o.option_value_reset === 1)

        const optionValueFind = props.option.values.find(
            (e) => e.option_value_id === props.currentOptionValueId
        )

        if (
            props.option.type === 'radio' &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== props.currentOptionValueId &&
            selectOptionsListHappy.value[props.option.option_id].selected_values
                .radio_option.option_value_id !== null
        ) {
            counterOptionValue.value = 0
            return
        } else if (
            (props.option.type === 'select' || props.option.type === 'checkbox') &&
            optionValueFindReset.length !== 0
        ) {
            const hasReset = optionValueFindReset.some((element) => {
                counterOptionValue.value = 1
                return (
                    element.option_value_reset === 1 &&
                    element.option_value_id === optionValueFind?.option_value_id
                )
            })

            if (hasReset) {
                counterOptionValue.value = 1
                return
            }
        }

        counterOptionValue.value = props.currentQuantity
    },
    { deep: true, immediate: true }
)

const ShowButtonPlus = computed(() => {
    if (!props.option?.values || !Array.isArray(props.option.values)) {
        return false
    }

    const option = props.option
    const optionId = option.option_id
    const optionData = selectOptionsListHappy.value[optionId]

    if (!optionData?.selected_values) {
        return true
    }
    const type = option.type
    const currentOptionValue = option.values.find(
        (e) => e.option_value_id === props.currentOptionValueId
    )

    if (type === 'radio') {
        const selectedOptionsListRadio =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .radio_option
        if (selectedOptionsListRadio.option_value_id !== props.currentOptionValueId) {
            counterOptionValue.value = 0
            return true
        } else {
            return false
        }
    } else if (type === 'checkbox') {
        const checkboxOptions =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                ?.checkbox_option

        const allSelected = [
            ...checkboxOptions.option_value_free,
            ...checkboxOptions.option_value_paid
        ]

        const isThisOptionSelected = allSelected.some(
            (item) => item.option_value_id === props.currentOptionValueId
        )

        if (isThisOptionSelected) {
            return false
        }

        counterOptionValue.value = 0
        return true
    } else if (type === 'select') {
        const selectedOptionListSelectFree =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .checkbox_option.option_value_free
        const selectedOptionListSelectPaid =
            selectOptionsListHappy.value[props.option.option_id]?.selected_values
                .checkbox_option.option_value_paid
        const findFreeReset = selectedOptionListSelectFree.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )
        const findPaidReset = selectedOptionListSelectPaid.find(
            (r) => r.option_value_id === props.currentOptionValueId
        )

        if (currentOptionValue?.reset === 1 && !findFreeReset && !findPaidReset) {
            return true
        }

        if (selectOptionsListHappy.value[props.option.option_id].selected_values) {
            const selectedIds = [
                ...selectedOptionListSelectFree,
                ...selectedOptionListSelectPaid
            ]
            if (
                type === 'select' &&
                props.maxQuantity !== selectedIds.length &&
                currentOptionValue?.reset === 0
            ) {
                return true
            }
        }
    }
})

function useIncrementOptionValueHappy(optionValueId: number, optionId: number) {
    const maxQuantity = props.maxQuantity
    const option = props.option
    if (maxQuantity === undefined || maxQuantity === null) {
        return
    }
    if (!option || !option.values || option.values.length === 0) {
        return
    }

    incrementOptionValueHappy(
        optionValueId,
        optionId,
        maxQuantity,
        counterOptionValue,
        option
    )
}

function useDecrementOptionValueHappy(optionValueId: number, optionId: number) {
    const option = props.option
    if (!option || !option.values || option.values.length === 0) {
        return
    }

    decrementOptionValueHappy(optionValueId, optionId, counterOptionValue, option)
}

onMounted(() => {
    if (
        props.option &&
        selectOptionsListHappy.value[props.option.option_id] === undefined
    ) {
        initializeOptionIfNeeded(props.option)
    }
})
</script>

<template>
    <section class="section-button-select-option-value-happy">
        <button
            class=""
            :class="{
                'button-minus-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
            @click.stop="
                useDecrementOptionValueHappy(
                    props.currentOptionValueId,
                    props.option.option_id
                )
            "
        >
            <svg
                class="svg-minus-happy"
                viewBox="0 0 15 2"
                xmlns="http://www.w3.org/2000/svg"
                shape-rendering="crispEdges"
            >
                <path d="M1 1H14" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>
        <p
            class=""
            :class="{
                'text-counter-option-value-happy': counterOptionValue > 0,
                hidden: counterOptionValue <= 0
            }"
        >
            {{ counterOptionValue }}
        </p>

        <button
            class="button-plus-option-value-happy"
            :class="{ 'button-plus-selected-happy': counterOptionValue > 0 }"
            @click.stop="
                useIncrementOptionValueHappy(
                    props.currentOptionValueId,
                    props.option.option_id
                )
            "
            v-if="ShowButtonPlus"
        >
            <svg
                class="svg-plus-happy"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                shape-rendering="crispEdges"
            >
                <path
                    d="M1 8.5H16M8.5 1V16"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>
    </section>
</template>

<style scoped>
.section-button-select-option-value-happy {
    @apply min-w-24 flex items-center justify-end gap-2;
}

.button-minus-option-value-happy {
    @apply flex items-center border rounded p-1 shadow-sm;
    @apply happy-light:border-button-yellow-happy-light happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:border-button-yellow-happy-light happy-dark:shadow-text-dark-happy-light;
    @apply happy-urban-dark-red:border-urban-dark happy-urban-dark-red:shadow-text-dark-happy-light;
    @apply happy-urban-light-red:border-urban-dark happy-urban-light-red:shadow-text-dark-happy-light;
    @apply happy-orange-light:border-urban-dark happy-orange-light:shadow-text-dark-happy-light;
    @apply happy-orange-dark:border-urban-dark happy-orange-dark:shadow-text-dark-happy-light;
    @apply happy-fresh-mint:border-urban-dark happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:border-urban-dark happy-bee-light:shadow-bg-happy-light happy-bee-light:bg-urban-dark;
    @apply happy-bee-dark:border-bee-yellow happy-bee-dark:shadow-urban-dark happy-bee-dark:bg-bee-yellow;
    @apply happy-earth-stone:border-bg-happy-light happy-earth-stone:shadow-bg-happy-light happy-earth-stone:bg-urban-dark;
    @apply happy-scorched-earth:border-scorched-earth-yellow happy-scorched-earth:shadow-bg-happy-light happy-scorched-earth:bg-scorched-earth-yellow;
    @apply happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark happy-sand-sky:bg-bg-sand;
    @apply happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark happy-gray-sand:bg-bg-gray-sand;
}
.svg-minus-happy {
    @apply w-4 h-4;
    @apply happy-light:stroke-text-dark-happy-light;
    @apply happy-dark:stroke-text-dark-happy-light;
    @apply happy-urban-dark-red:stroke-urban-dark;
    @apply happy-urban-light-red:stroke-urban-dark;
    @apply happy-orange-light:stroke-urban-dark;
    @apply happy-orange-dark:stroke-urban-dark;
    @apply happy-fresh-mint:stroke-urban-dark;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-urban-dark;
    @apply happy-earth-stone:stroke-bg-happy-light;
    @apply happy-scorched-earth:stroke-urban-dark;
    @apply happy-sand-sky:stroke-urban-dark;
    @apply happy-gray-sand:stroke-urban-dark;
}

.text-counter-option-value-happy {
    @apply text-lg text-center align-baseline w-6 font-semibold;
    @apply happy-light:text-text-dark-happy-light;
    @apply happy-dark:text-text-dark-happy-light;
    @apply happy-urban-dark-red:text-urban-dark;
    @apply happy-urban-light-red:text-urban-dark;
    @apply happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:text-urban-dark;
    @apply happy-fresh-mint:text-urban-dark;
    @apply happy-bee-light:text-bg-happy-light;
    @apply happy-bee-dark:text-bg-happy-light;
    @apply happy-earth-stone:text-bg-happy-light;
    @apply happy-scorched-earth:text-bg-happy-light;
    @apply happy-sand-sky:text-bg-happy-light;
    @apply happy-gray-sand:text-urban-dark;
}

.button-plus-option-value-happy {
    @apply flex items-center p-1 shadow-sm rounded border;
    @apply happy-light:bg-hover-yellow-happy happy-light:border-hover-yellow-happy happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:bg-hover-yellow-happy happy-dark:border-hover-yellow-happy happy-dark:shadow-bg-gray-sand;
    @apply happy-urban-dark-red:bg-urban-dark happy-urban-dark-red:border-urban-dark happy-urban-dark-red:shadow-bg-gray-sand;
    @apply happy-urban-light-red:bg-urban-dark happy-urban-light-red:border-urban-dark happy-urban-light-red:shadow-bg-gray-sand;
    @apply happy-orange-light:bg-urban-dark happy-orange-light:border-urban-dark happy-orange-light:shadow-bg-gray-sand;
    @apply happy-orange-dark:bg-urban-dark happy-orange-dark:border-urban-dark happy-orange-dark:shadow-bg-gray-sand;
    @apply happy-fresh-mint:bg-urban-dark happy-fresh-mint:border-urban-dark happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:bg-urban-dark happy-bee-light:border-urban-dark happy-bee-light:shadow-urban-dark;
    @apply happy-bee-dark:bg-bee-yellow happy-bee-dark:border-bee-yellow happy-bee-dark:shadow-bg-happy-light;
    @apply happy-earth-stone:bg-urban-dark happy-earth-stone:border-bg-happy-light happy-earth-stone:shadow-bg-happy-light;
    @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-red happy-scorched-earth:shadow-bg-happy-light;
    @apply happy-sand-sky:bg-bg-sand happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:bg-bg-gray-sand happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark;
}

.svg-plus-happy {
    @apply w-4 h-4 stroke-text-dark-happy-light;
    @apply happy-light:stroke-text-dark-happy-light;
    @apply happy-dark:stroke-text-dark-happy-light;
    @apply happy-urban-dark-red:stroke-bg-happy-light;
    @apply happy-urban-light-red:stroke-bg-happy-light;
    @apply happy-orange-light:stroke-bg-happy-light;
    @apply happy-orange-dark:stroke-bg-happy-light;
    @apply happy-fresh-mint:stroke-bg-happy-light;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-urban-dark;
    @apply happy-earth-stone:stroke-bg-happy-light;
    @apply happy-scorched-earth:stroke-bg-happy-light;
    @apply happy-sand-sky:stroke-urban-dark;
    @apply happy-gray-sand:stroke-urban-dark;
}
.hidden {
    display: none;
}

.button-plus-option-value-happy:active,
.button-plus-option-value-happy:focus {
    @apply happy-light:bg-hover-yellow-happy happy-light:border-hover-yellow-happy;
    @apply happy-dark:bg-hover-yellow-happy happy-dark:border-hover-yellow-happy;
    @apply happy-urban-dark-red:bg-urban-red;
    @apply happy-urban-light-red:bg-urban-red;
    @apply happy-orange-light:bg-bg-orange-dark;
    @apply happy-orange-dark:bg-bg-orange-dark;
    @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:border-bg-mint;
    @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:border-urban-dark;
    @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:border-bee-yellow;
    @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:border-bg-happy-light;
    @apply happy-scorched-earth:bg-bg-scorched-earth happy-scorched-earth:border-scorched-earth-red;
    @apply happy-sand-sky:bg-sand-blue happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark;
}

.button-plus-option-value-happy:active .svg-plus-happy,
.button-plus-option-value-happy:focus .svg-plus-happy {
    @apply happy-light:stroke-bg-happy-light;
    @apply happy-dark:stroke-bg-happy-light;
    @apply happy-urban-dark-red:stroke-bg-happy-light;
    @apply happy-urban-light-red:stroke-bg-happy-light;
    @apply happy-orange-light:stroke-white;
    @apply happy-orange-dark:stroke-white;
    @apply happy-fresh-mint:stroke-bg-happy-light;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-bg-happy-light;
    @apply happy-earth-stone:stroke-bg-happy-light;
    @apply happy-scorched-earth:stroke-bg-happy-light;
    @apply happy-sand-sky:stroke-urban-dark;
    @apply happy-gray-sand:stroke-urban-dark;
}

.button-minus-option-value-happy:active,
.button-minus-option-value-happy:focus {
    @apply happy-light:bg-hover-yellow-happy happy-light:border-hover-yellow-happy;
    @apply happy-dark:bg-hover-yellow-happy happy-dark:border-hover-yellow-happy;
    @apply happy-urban-dark-red:bg-urban-red;
    @apply happy-urban-light-red:bg-urban-red;
    @apply happy-orange-light:bg-bg-orange-dark;
    @apply happy-orange-dark:bg-bg-orange-dark;
    @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:border-bg-mint;
    @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:border-urban-dark;
    @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:border-bee-yellow;
    @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:border-bg-happy-light;
    @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
    @apply happy-sand-sky:bg-sand-blue happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark;
}
.button-minus-option-value-happy:active .svg-minus-happy,
.button-minus-option-value-happy:focus .svg-minus-happy {
    @apply happy-light:stroke-bg-happy-light;
    @apply happy-dark:stroke-bg-happy-light;
    @apply happy-urban-dark-red:stroke-bg-happy-light;
    @apply happy-urban-light-red:stroke-bg-happy-light;
    @apply happy-orange-light:stroke-white;
    @apply happy-orange-dark:stroke-white;
    @apply happy-fresh-mint:stroke-bg-happy-light;
    @apply happy-bee-light:stroke-bg-happy-light;
    @apply happy-bee-dark:stroke-bg-happy-light;
    @apply happy-earth-stone:stroke-bg-happy-light;
    @apply happy-scorched-earth:stroke-bg-happy-light;
    @apply happy-sand-sky:stroke-urban-dark;
    @apply happy-gray-sand:stroke-urban-dark;
}

.button-plus-selected-happy {
    @apply happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:shadow-text-dark-happy-light;
    @apply happy-urban-dark-red:shadow-urban-dark;
    @apply happy-urban-light-red:shadow-urban-dark;
    @apply happy-orange-light:shadow-urban-dark;
    @apply happy-orange-dark:shadow-urban-dark;
    @apply happy-fresh-mint:shadow-urban-dark;
    @apply happy-bee-light:shadow-bg-happy-light;
    @apply happy-bee-dark:shadow-urban-dark;
    @apply happy-earth-stone:shadow-bg-happy-light;
    @apply happy-scorched-earth:bg-scorched-earth-yellow happy-scorched-earth:border-scorched-earth-yellow;
    @apply happy-sand-sky:shadow-urban-dark;
    @apply happy-gray-sand:shadow-urban-dark;
}

.button-plus-selected-happy .svg-plus-happy {
    @apply happy-scorched-earth:stroke-urban-dark;
}

.button-plus-selected-happy:active,
.button-plus-selected-happy:focus {
    @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
}

.button-plus-selected-happy:active .svg-plus-happy,
.button-plus-selected-happy:focus .svg-plus-happy {
    @apply happy-scorched-earth:stroke-bg-happy-light;
}

@media (hover: hover) {
    .button-plus-option-value-happy:hover {
        @apply happy-light:bg-hover-yellow-happy happy-light:border-hover-yellow-happy;
        @apply happy-dark:bg-hover-yellow-happy happy-dark:border-hover-yellow-happy;
        @apply happy-urban-dark-red:bg-urban-red happy-urban-dark-red:border-urban-dark;
        @apply happy-urban-light-red:bg-urban-red happy-urban-light-red:border-urban-dark;
        @apply happy-orange-light:bg-bg-orange-dark happy-orange-light:border-urban-dark;
        @apply happy-orange-dark:bg-bg-orange-dark happy-orange-dark:border-urban-dark;
        @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:border-bg-mint;
        @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:border-urban-dark;
        @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:border-urban-dark;
        @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:border-bee-yellow;
        @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:border-bg-happy-light;
        @apply happy-scorched-earth:bg-bg-scorched-earth happy-scorched-earth:border-scorched-earth-red;
        @apply happy-sand-sky:bg-sand-blue happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark;
    }

    .button-plus-selected-happy:hover {
        @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
    }
    .button-plus-selected-happy:hover .svg-plus-happy {
        @apply happy-scorched-earth:stroke-bg-happy-light;
    }

    .button-plus-option-value-happy:hover .svg-plus-happy {
        @apply happy-light:stroke-bg-happy-light;
        @apply happy-dark:stroke-bg-happy-light;
        @apply happy-urban-dark-red:stroke-bg-happy-light;
        @apply happy-urban-light-red:stroke-bg-happy-light;
        @apply happy-orange-light:stroke-white;
        @apply happy-orange-dark:stroke-white;
        @apply happy-fresh-mint:stroke-bg-happy-light;
        @apply happy-bee-light:stroke-bg-happy-light;
        @apply happy-bee-dark:stroke-bg-happy-light;
        @apply happy-earth-stone:stroke-bg-happy-light;
        @apply happy-scorched-earth:stroke-bg-happy-light;
        @apply happy-sand-sky:stroke-urban-dark;
        @apply happy-gray-sand:stroke-urban-dark;
    }
    .button-minus-option-value-happy:hover {
        @apply happy-light:bg-hover-yellow-happy happy-light:border-hover-yellow-happy;
        @apply happy-dark:bg-hover-yellow-happy happy-dark:border-hover-yellow-happy;
        @apply happy-urban-dark-red:bg-urban-red happy-urban-dark-red:border-urban-dark;
        @apply happy-urban-light-red:bg-urban-red happy-urban-light-red:border-urban-dark;
        @apply happy-orange-light:bg-bg-orange-dark happy-orange-light:border-urban-dark;
        @apply happy-orange-dark:bg-bg-orange-dark happy-orange-dark:border-urban-dark;
        @apply happy-fresh-mint:bg-bg-mint-dark happy-fresh-mint:border-bg-mint;
        @apply happy-bee-light:bg-bee-yellow-dark happy-bee-light:border-urban-dark;
        @apply happy-bee-dark:bg-bee-yellow-dark happy-bee-dark:border-bee-yellow;
        @apply happy-earth-stone:bg-earth-card-dark happy-earth-stone:border-bg-happy-light;
        @apply happy-scorched-earth:bg-scorched-earth-red happy-scorched-earth:border-scorched-earth-yellow;
        @apply happy-sand-sky:bg-sand-blue happy-sand-sky:border-bg-sand happy-sand-sky:shadow-urban-dark;
        @apply happy-gray-sand:bg-gray-sand-gray-dark happy-gray-sand:border-bg-gray-sand happy-gray-sand:shadow-urban-dark;
    }
    .button-minus-option-value-happy:hover .svg-minus-happy {
        @apply happy-light:stroke-bg-happy-light;
        @apply happy-dark:stroke-bg-happy-light;
        @apply happy-urban-dark-red:stroke-white;
        @apply happy-urban-light-red:stroke-white;
        @apply happy-orange-light:stroke-white;
        @apply happy-orange-dark:stroke-white;
        @apply happy-fresh-mint:stroke-bg-happy-light;
        @apply happy-bee-light:stroke-bg-happy-light;
        @apply happy-bee-dark:stroke-bg-happy-light;
        @apply happy-earth-stone:stroke-bg-happy-light;
        @apply happy-scorched-earth:stroke-bg-happy-light;
        @apply happy-sand-sky:stroke-urban-dark;
        @apply happy-gray-sand:stroke-urban-dark;
    }
}
</style>
